import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { MapPin, Lock } from "lucide-react";
import { setPageMeta } from "../lib/seo";
import { firstPhoneDigits } from "../lib/phone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicOfferPage() {
  const { offerId } = useParams();
  const [offer, setOffer] = useState(null);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/oferta/${offerId}`)
      .then((res) => {
        setOffer(res.data);
        const o = res.data;
        setPageMeta({
          title: `${o.title}${o.city ? ` — ${o.city}` : ""} | Profissional disponível | HeveaTalent`,
          description: `Profissional da heveicultura disponível para trabalho: ${o.title}. Veja o contato grátis no HeveaTalent.`,
          canonical: `https://heveatalent.com.br/oferta/${offerId}`,
        });
        if (localStorage.getItem("token")) {
          axios
            .get(`${API}/oferta/${offerId}/contact`, {
              headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            })
            .then((r) => setContact(r.data))
            .catch((e) => {
              if (e.response?.status === 401) localStorage.removeItem("token");
            });
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [offerId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center">
        <p className="text-green-800">Carregando...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center" data-testid="public-offer-not-found">
          <CardContent className="pt-8 pb-8">
            <p className="text-4xl mb-3">🌳</p>
            <h2 className="text-xl font-bold text-gray-900">Esta oferta foi encerrada</h2>
            <p className="text-sm text-gray-600 mt-2">O profissional pode já ter conseguido serviço — veja quem mais está disponível.</p>
            <Link to="/balcao">
              <Button className="mt-4 bg-green-700 hover:bg-green-800" data-testid="public-offer-goto-balcao">
                Ver profissionais disponíveis →
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const digits = firstPhoneDigits(contact?.contact);

  return (
    <div className="min-h-screen bg-green-50" data-testid="public-offer-page">
      <header className="bg-green-900 text-white py-4 px-4 text-center">
        <Link to="/" className="font-bold text-lg">🌳 HeveaTalent</Link>
        <p className="text-green-200 text-xs">Balcão de Empregos da Heveicultura — gratuito</p>
      </header>

      <main className="max-w-md mx-auto p-4 pb-10">
        <Card className="border-2 border-amber-400" data-testid="public-offer-card">
          <CardContent className="pt-5">
            <Badge className="bg-amber-600 text-white text-[10px]">PROFISSIONAL DISPONÍVEL</Badge>
            <h1 className="text-xl font-bold text-gray-900 mt-2">{offer.title}</h1>
            {offer.city && (
              <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                <MapPin className="w-4 h-4 text-green-700" /> {offer.city}
              </p>
            )}
            {offer.confirmed_at && (
              <span className="inline-block mt-2 px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs font-semibold" data-testid="public-offer-confirmed">
                ✅ Disponibilidade confirmada em {new Date(offer.confirmed_at).toLocaleDateString("pt-BR")}
              </span>
            )}
            {offer.summary && <p className="text-sm text-gray-700 mt-3">{offer.summary}</p>}
            {offer.confirm_details && (offer.confirm_details.plantas || offer.confirm_details.facas) && (
              <p className="text-xs text-gray-600 mt-2" data-testid="public-offer-details">
                {offer.confirm_details.plantas ? `🌱 ${offer.confirm_details.plantas} plantas` : ""}
                {offer.confirm_details.plantas && offer.confirm_details.facas ? " · " : ""}
                {offer.confirm_details.facas ? `🔪 ${offer.confirm_details.facas}` : ""}
              </p>
            )}
            {offer.reference?.name && (
              <p className="text-xs text-emerald-700 font-medium mt-2" data-testid="public-offer-reference">
                ⭐ Referência: {offer.reference.name}{offer.reference.farm ? ` — ${offer.reference.farm}` : ""}
              </p>
            )}

            {contact ? (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4" data-testid="public-offer-contact">
                <p className="text-xs font-semibold text-green-900 uppercase">📞 Contato direto (grátis, sem intermediário)</p>
                {(contact.name || offer.name) && <p className="text-sm text-gray-800 mt-1">{contact.name || offer.name}</p>}
                {contact.contact ? (
                  <>
                    <p className="text-lg font-bold text-green-900" data-testid="public-offer-phone">{contact.contact}</p>
                    {digits.length >= 10 && (
                      <a
                        href={`https://wa.me/55${digits}?text=${encodeURIComponent(`Olá! Vi sua oferta de trabalho "${offer.title}" no Balcão da HeveaTalent e tenho interesse.`)}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button className="mt-2 w-full bg-green-600 hover:bg-green-700" data-testid="public-offer-whatsapp-btn">
                          💬 Chamar no WhatsApp
                        </Button>
                      </a>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-600 mt-1">Este profissional não informou telefone — fale com ele pelo chat da plataforma.</p>
                )}
              </div>
            ) : (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-center" data-testid="public-offer-register-gate">
                <Lock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                <p className="font-semibold text-amber-900">O contato é exclusivo para cadastrados</p>
                <p className="text-sm text-amber-700 mt-1">
                  Cadastre-se grátis (leva 1 minuto) e veja o telefone deste e de todos os profissionais disponíveis.
                </p>
                <Link
                  to="/?cadastro=vaga"
                  onClick={() => localStorage.setItem("pending_oferta", JSON.stringify({ id: offerId, ts: Date.now() }))}
                >
                  <Button className="mt-3 w-full sm:w-auto bg-green-700 hover:bg-green-800" data-testid="public-offer-register-btn">
                    Cadastrar grátis e ver o contato →
                  </Button>
                </Link>
                <p className="text-xs text-amber-600 mt-2">Depois do cadastro você volta direto para esta página. 😉</p>
              </div>
            )}
            <p className="text-[11px] text-gray-500 mt-2">
              ⚠️ Combine os detalhes diretamente e confirme as informações antes de fechar.
            </p>
          </CardContent>
        </Card>

        <div className="mt-5 bg-white border border-green-200 rounded-2xl p-5" data-testid="public-offer-benefits">
          <p className="font-bold text-green-900 text-sm">🌳 Tem seringal? A HeveaTalent é grátis:</p>
          <ul className="mt-2 space-y-1.5 text-sm text-gray-700">
            <li>🤝 <b>Balcão de Empregos</b> — anuncie vagas e encontre profissionais</li>
            <li>📅 <b>Calendário e gestão</b> — anote produção, chuvas e dados da sangria</li>
            <li>📰 <b>Comunidade</b> — notícias, dicas e preços da borracha</li>
          </ul>
          <a
            href="/?cadastro=vaga"
            onClick={() => localStorage.setItem("pending_oferta", JSON.stringify({ id: offerId, ts: Date.now() }))}
            className="mt-3 block w-full text-center bg-green-700 hover:bg-green-800 text-white font-bold py-3 rounded-xl text-sm"
            data-testid="public-offer-register-cta"
          >
            Criar minha conta grátis →
          </a>
          <Link to="/balcao" className="mt-2 block text-center text-green-700 text-xs underline" data-testid="public-offer-balcao-link">
            Ver todos os profissionais e vagas no Balcão →
          </Link>
        </div>
      </main>
    </div>
  );
}
